import React from "react";
import { useDispatch } from "react-redux";

import makeStyles from "@material-ui/core/styles/makeStyles";
import MuiDrawer from "@material-ui/core/Drawer";
import { Typography } from "@material-ui/core";

// icons
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

// perfect scrollbar
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";

import { Button, showNotification } from "jazasoft";
import Table, { NativeTextInput } from "../../../../components/Table";

import MuiTable from "../../../../components/MuiTable";

import { isEmpty, defaultParsePaste, isTrue } from "../../../../utils/helpers";
import useSafeState from "../../../../hooks/useSafeState";
import useDeepEffect from "../../../../hooks/useDeepEffect";
import useDeepMemo from "../../../../hooks/useDeepMemo";
import { humanizeMarkerName } from "../../cutPlanHelper";
import { CutPlanType, LotType } from "../../../../constants";
import { useMutation } from "../../../../hooks";
import { UPDATE_TASK } from "../../../../reducers/scheduledTaskReducer";
import { REGEX_SPLICE_POINT } from "../../../../utils/regex";

const validate = rows => {
  if (isEmpty(rows)) return [];
  let errors = rows.map(row => {
    const pointList = Object.entries(row)
      .filter(([key, value]) => key.startsWith("point_") && !isEmpty(value))
      .map(([key, value]) => {
        return { key, value: REGEX_SPLICE_POINT.test(value) ? undefined : `Invalid Value. It must be comma separated decimal pair. e.g. 1.0,2.0` };
      });
    return pointList.filter(p => !isEmpty(p.value)).reduce((acc, p) => ({ ...acc, [p.key]: p.value }), {});
  });

  if (errors.every(err => isEmpty(err))) {
    errors = rows.map(row => {
      const pointList = Object.entries(row)
        .filter(([key, value]) => key.startsWith("point_") && !isEmpty(value))
        .map(([key, value]) => {
          const [start, end] = `${value}`.includes("-")
            ? value
                .split("-")
                .map(e => e.trim())
                .map(Number)
            : value
                .split(",")
                .map(e => e.trim())
                .map(Number);
          return { dataKey: key, pointNo: Number(key.split("_")[1]), start, end };
        });

      let error = {};

      for (let i = 0; i < pointList.length; i++) {
        const prevPoint = i > 0 ? pointList[i - 1] : null;
        const point = pointList[i];

        let errList = [];
        // Start must be less than end
        if (point.start >= point.end) {
          errList.push(`End value must be greater than Start value.`);
        }

        // First point start must be 0
        if (i === 0 && point.start !== 0) {
          errList.push(`First point start value must be 0.`);
        }
        // Last point end must be marker length
        if (i === pointList.length - 1 && point.end !== row.markerLength) {
          errList.push(`Last point end value must be equal to Marker Length = ${row.markerLength}.`);
        }

        // Splice points must be between 0 and marker length
        if (point.start < 0 || point.start > row.markerLength) {
          errList.push(`Start value (${point.start}) must be between 0 and Marker Length (${row.markerLength}).`);
        }
        if (point.end < 0 || point.end > row.markerLength) {
          errList.push(`End value (${point.end}) must be between 0 and Marker Length (${row.markerLength}).`);
        }

        // Validate with previous point
        if (prevPoint && point.start > prevPoint.end) {
          errList.push(`Start value (${point.start}) must be less than equal to previous point end value (${prevPoint.end}).`);
        }
        if (prevPoint && point.start < prevPoint.start) {
          errList.push(`Start value (${point.start}) must be greater than previous point start value (${prevPoint.start}).`);
        }

        if (errList.length > 0) {
          error[point.dataKey] = errList.join(" \n");
        }
      }

      return error;
    });
  }

  return errors;
};

const errorColumns = [
  { dataKey: "markerNo", title: "M. No", align: "center" },
  { dataKey: "point", title: "Point", align: "center" },
  { dataKey: "error", title: "Error" }
];

const useStyles = makeStyles(theme => ({
  drawer: ({ drawerWidth }) => ({
    width: drawerWidth,
    flexShrink: 0
  }),
  drawerPaper: ({ drawerWidth }) => ({
    width: drawerWidth
  }),
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  table: {
    whiteSpace: "pre",
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  rowCell: {
    padding: 0,
    fontSize: 12,
    "&:last-child": {
      padding: 0,
      fontSize: 12
    }
  }
}));

const SplicePointDrawer = props => {
  const { open, order, cutOrder, noOfLots, partCount, onDrawerClose, onRefresh, drawerWidth = 1000 } = props;

  const classes = useStyles({ drawerWidth });

  const [pointCount, setPointCount] = useSafeState(5);
  const [rows, setRows] = useSafeState([]);
  const [errors, setErrors] = useSafeState([]);

  const [mutate] = useMutation();
  const dispatch = useDispatch();

  const algorithmicPlan = cutOrder?.orderConfig?.setting?.cutPlanType === CutPlanType.ALGORITHMIC;

  useDeepEffect(() => {
    if (open) {
      const markerList = cutOrder?.markerList || [];
      const rows = markerList
        .sort((a, b) => a.markerNo - b.markerNo)
        .map(marker => {
          const splicePoints = !isEmpty(marker.splicePoints) ? JSON.parse(marker.splicePoints) : [];
          return {
            id: marker.id,
            completed: marker.completed,
            markerNo: marker.markerNo,
            markerName: humanizeMarkerName(marker.name).replace(/ \| /g, "\n"),
            markerLength: marker.markerLength,
            length: !isEmpty(marker.length) && marker.length > 0 ? `${marker.markerLength} m` : "",
            ...splicePoints.reduce((acc, point) => ({ ...acc, [`point_${point.pointNo}`]: `${point.start},${point.end}` }), {})
          };
        });
      setRows(rows);
    } else {
      setRows([]);
      setErrors([]);
    }
  }, [open, cutOrder]);

  const onChange =
    ({ rowIdx, column }) =>
    e => {
      const value = e?.target?.value || e?.value;
      setRows(prevRows => {
        let newRows = [...prevRows];
        const data = defaultParsePaste(value) || [];
        if (data.length === 0 || (data.length === 1 && data[0].length === 1)) {
          const row = { ...prevRows[rowIdx], [column.dataKey]: value };
          newRows.splice(rowIdx, 1, row);
        }
        return newRows;
      });
    };

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "markerNo", title: "M. No", align: "center" },
      { dataKey: "markerName", title: "Marker Name" },
      { dataKey: "length", title: "Marker Length", align: "center" },
      ...Array.from({ length: pointCount }, (_, i) => i + 1).map(i => ({
        dataKey: `point_${i}`,
        title: `Point ${i}`,
        align: "center",
        element: (
          <NativeTextInput
            style={{ textAlign: "center" }}
            onChange={onChange}
            disabled={({ record }) => record?.markerLength <= 0 || (algorithmicPlan && record.completed)}
          />
        )
      }))
    ];

    return cols;
  }, [pointCount, algorithmicPlan]);

  const onSave = () => {
    const errors = validate(rows);
    setErrors(errors);
    const hasErrors = errors.some(err => !isEmpty(err));
    if (hasErrors) {
      dispatch(showNotification("Please fix validation errors before saving.", { type: "danger" }));
      return;
    }

    const markerList = cutOrder?.markerList || [];
    const markers = rows
      .map(row => {
        const marker = markerList.find(m => m.id === row.id) || {};
        const pointList = Object.entries(row)
          .filter(([key, value]) => key.startsWith("point_") && !isEmpty(value))
          .map(([key, value]) => {
            const [start, end] = `${value}`.includes("-")
              ? value
                  .split("-")
                  .map(e => e.trim())
                  .map(Number)
              : value
                  .split(",")
                  .map(e => e.trim())
                  .map(Number);
            return { pointNo: Number(key.split("_")[1]), start, end };
          });
        const splicePoints = pointList?.length > 0 ? JSON.stringify(pointList) : undefined;
        const plies = marker.markerPlyList?.reduce((acc, mp) => acc + mp.plannedPlies, 0) || 0;
        return { ...marker, splicePoints, plies };
      })
      .filter(m => !isEmpty(m.id));

    const planningDto = {
      cutOrderId: cutOrder?.id,
      poRef: order?.poRef,
      colors: cutOrder?.colors,
      styles: cutOrder?.styles,
      inseams: cutOrder?.inseams,
      destinations: cutOrder?.destinations,
      deliveries: cutOrder?.deliveries,
      flowRefs: cutOrder?.flowRefs,
      customerFlowRefs: cutOrder?.customerFlowRefs,
      productName: cutOrder?.product?.name,
      part: cutOrder?.part,
      lot: cutOrder?.lot,
      noOfLots,
      partCount,
      markers,
      manual: cutOrder?.lotType === LotType.PRE_RUN || !algorithmicPlan
    };

    const options = {
      url: `orders/${order?.id}/markers`,
      method: "put",
      data: planningDto,
      params: { action: "default", flowId: cutOrder?.id }
    };

    const allCompleted = markers.filter(e => e.plies > 1).every(el => !isEmpty(el?.efficiency) && !isEmpty(el?.length) && !isEmpty(el?.splicePoints));

    mutate(options, {
      onSuccess: data => {
        dispatch({ type: UPDATE_TASK, payload: { task: data } });
        allCompleted && dispatch(showNotification("Task queued. you will be notified once completed"));
        onRefresh && onRefresh();
        onDrawerClose && onDrawerClose();
      },
      onFailure: error => {
        console.log({ error });
      }
    });
  };

  const errorRows = errors.flatMap((err, rowIdx) => {
    return Object.entries(err)
      .map(([key, value]) => {
        return {
          markerNo: rows[rowIdx]?.markerNo,
          point: key.replace("point_", "Point "),
          error: value
        };
      })
      .filter(e => !isEmpty(e.error));
  });

  const allCompleted = cutOrder?.markerList
    ?.filter(m => m.markerPlyList?.reduce((acc, mp) => acc + mp.plannedPlies, 0) > 1)
    .every(m => isTrue(m.completed));

  return (
    <MuiDrawer
      className={classes.drawer}
      anchor="right"
      open={open}
      classes={{
        paper: classes.drawerPaper
      }}
      onClose={onDrawerClose}>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onDrawerClose}>
          <ChevronRightIcon />
        </IconButton>
        <Typography variant="h5">Splice Points</Typography>
      </div>
      {/* <Divider /> */}

      <div>
        <div>
          <PerfectScrollbar>
            <Table
              key={`${JSON.stringify(errorRows)}`}
              classes={{ table: classes.table, rowCell: classes.rowCell }}
              columns={columns}
              rows={rows}
              setRows={setRows}
              i18nKey="resources.orders.fields.flowInfo"
              headerCellStyle={column => ({
                width: column.dataKey === "markerNo" ? 60 : column.dataKey === "markerName" ? 300 : undefined,
                fontWeight: "bold"
              })}
              cellStyle={({ column, rowIdx }) => {
                const error = !isEmpty(errors[rowIdx]) ? errors[rowIdx][column.dataKey] : undefined;
                return {
                  padding: column.dataKey === "markerName" ? "4px 8px" : 2,
                  borderBottom: error ? "1px solid red" : undefined
                };
              }}
            />
          </PerfectScrollbar>
        </div>
        {!allCompleted && (
          <div style={{ padding: "16px 16px", display: "flex", justifyContent: "flex-start", gap: "12px" }}>
            <Button variant="contained" color="primary" onClick={onSave}>
              Save
            </Button>
            <Button variant="outlined" color="primary" onClick={() => setPointCount(prev => prev + 1)}>
              Add Point
            </Button>
          </div>
        )}

        {!isEmpty(errorRows) && (
          <div style={{ padding: "0 16px 16px 16px" }}>
            <MuiTable title="Errors" toolbar columns={errorColumns} rows={errorRows} cellLength={200} cellOverFlow="wrap" />
          </div>
        )}
      </div>
    </MuiDrawer>
  );
};

export default SplicePointDrawer;
